import store from "../store";
import { AUTH_CONTEXT_TYPE } from "./authPortal";
import { getSessionPortal } from "./Auth";
import { readPersistedLoginForPortal } from "./authStorage";
import { ROUTE_PATHS } from "./RoutesPaths";

export function getClientPortalLogin() {
  const portal = getSessionPortal();
  if (portal !== AUTH_CONTEXT_TYPE.CLIENT) {
    return {};
  }
  try {
    const fromRedux = store.getState()?.auth?.login;
    if (fromRedux?.auth_token || fromRedux?.id) {
      return fromRedux;
    }
  } catch {
    /* store unavailable */
  }
  return readPersistedLoginForPortal(AUTH_CONTEXT_TYPE.CLIENT);
}

export function getClientCompanies(login) {
  const source = login ?? getClientPortalLogin();
  return Array.isArray(source?.companies) ? source.companies : [];
}

export function getClientSelectedCompany(login) {
  const source = login ?? getClientPortalLogin();
  const selected = source?.selected_company;
  if (!selected?.id) return null;
  return selected;
}

/** Normalize company name for selects and cards. */
export function normalizeClientCompanyOption(company) {
  if (!company || typeof company !== "object") return company;

  return {
    ...company,
    name:
      company.name ||
      company.name_en ||
      company.name_ar ||
      String(company.id ?? ""),
  };
}

export function isSameClientCompanyId(leftId, rightId) {
  return (
    leftId != null && rightId != null && String(leftId) === String(rightId)
  );
}

/** Company row from `login.companies` matching the active workspace selection. */
export function resolveClientSelectedCompanyOption(login) {
  const companies = getClientCompanies(login);
  if (!companies.length) return null;

  const selected = getClientSelectedCompany(login);
  if (selected?.id != null) {
    const match = companies.find((company) =>
      isSameClientCompanyId(company.id, selected.id),
    );
    if (match) {
      return normalizeClientCompanyOption({
        ...match,
        name: match.name || selected.name,
      });
    }
    return normalizeClientCompanyOption(selected);
  }

  return normalizeClientCompanyOption(companies[0]) || null;
}

export function applyClientCompanySelection(login, company) {
  if (!company?.id) return login;
  return {
    ...login,
    selected_company: {
      id: company.id,
      name: company.name,
      image_url: company.image_url ?? null,
    },
  };
}

/** Refresh `login.companies` (and selected workspace label) after locale-driven refetch. */
export function mergeClientCompaniesIntoLogin(login, companyRows) {
  if (!login || typeof login !== "object") return login;
  if (!Array.isArray(companyRows) || companyRows.length === 0) return login;

  const selectedId = getClientSelectedCompany(login)?.id;
  const nextLogin = {
    ...login,
    companies: companyRows,
  };

  if (selectedId == null) return nextLogin;

  const match = companyRows.find((company) =>
    isSameClientCompanyId(company.id, selectedId),
  );
  return match ? applyClientCompanySelection(nextLogin, match) : nextLogin;
}

export function clearClientCompanySelection(login) {
  if (!login || typeof login !== "object") return login;
  const next = { ...login };
  delete next.selected_company;
  return next;
}

export function clientHasMultipleCompanies(login) {
  return getClientCompanies(login).length >= 2;
}

export function clientRequiresCompanySelection(login) {
  if (isClientMustChangePasswordLocked(login)) return false;
  return (
    clientHasMultipleCompanies(login) && !getClientSelectedCompany(login)
  );
}

export function resolveClientPostLoginRoute(login) {
  if (isClientMustChangePasswordLocked(login)) {
    return ROUTE_PATHS.clientResetPassword;
  }
  const companies = getClientCompanies(login);
  if (companies.length === 0) {
    return ROUTE_PATHS.clientPortalDashboard;
  }
  if (companies.length === 1 || getClientSelectedCompany(login)) {
    return ROUTE_PATHS.clientPortalEvents;
  }
  return ROUTE_PATHS.clientSelectCompany;
}

export function getClientSelectedCompanyId(login) {
  return getClientSelectedCompany(login)?.id ?? null;
}

export const PENDING_CLIENT_RESET_PASSWORD_FLOW_KEY =
  "pending_client_reset_password_flow";

export function isClientMustChangePasswordLocked(admin) {
  if (getSessionPortal() !== AUTH_CONTEXT_TYPE.CLIENT) return false;
  const source =
    admin && admin.auth_token ? admin : getClientPortalLogin();
  return Boolean(source?.must_change_password);
}

export function isUnderClientResetPasswordPath(pathname) {
  return (
    typeof pathname === "string" &&
    pathname.startsWith(ROUTE_PATHS.clientResetPassword)
  );
}

export function isAllowedPathWhileClientMustChangePassword(pathname) {
  return isUnderClientResetPasswordPath(pathname);
}

export function getClientResetPasswordLockRedirectState(login) {
  const source =
    login && login.auth_token ? login : getClientPortalLogin();
  return {
    advanceToStep2: true,
    email: typeof source?.email === "string" ? source.email : "",
    resetPasswordFormKey: Date.now(),
  };
}

export function getClientMustChangePasswordLockRedirect(login) {
  return {
    pathname: ROUTE_PATHS.clientResetPassword,
    state: getClientResetPasswordLockRedirectState(login),
  };
}
